import { esc, formatDist, safeHref } from '../utils/formatters';
import { centroidOfGeometry, padBounds } from '../utils/coords';
import { addOrUpdateGeoJsonSource, registerCursorHandlers, removeSourceAndLayers } from '../utils/maplibre-helpers';
import { createMapHighlightPulsator, getMapHighlightPaint } from '../utils/map-highlight';

var HIGHLIGHT_SOURCE_ID = 'src-persona-highlight';
var HIGHLIGHT_LAYER_IDS = [
    'lyr-persona-highlight-fill',
    'lyr-persona-highlight-polygon-casing',
    'lyr-persona-highlight-line',
    'lyr-persona-highlight-linestring-casing',
    'lyr-persona-highlight-linestring',
    'lyr-persona-highlight-circle'
];
var HIGHLIGHT_CLICK_LAYER_IDS = [
    'lyr-persona-highlight-fill',
    'lyr-persona-highlight-line',
    'lyr-persona-highlight-linestring',
    'lyr-persona-highlight-circle'
];

export function createPersonaModule(config) {
    var messages = config.messages || {};
    var abortController = null;
    var detailAbortControllers = {};
    var detailRequests = {};
    var active = false;
    var loading = false;
    var activeData = null;
    var activeLatLng = null;
    var expandedPersonas = {};
    var expandedSignals = {};
    var expandedGroups = {};
    var signalDetailsCache = {};
    var signalLoading = {};
    var signalErrors = {};
    var highlightInteractionsBound = false;
    var highlightPopup = null;
    var highlightPulsator = createMapHighlightPulsator({
        getMap: config.getMap,
        layerIds: {
            fill: 'lyr-persona-highlight-fill',
            polygonCasing: 'lyr-persona-highlight-polygon-casing',
            polygonLine: 'lyr-persona-highlight-line',
            lineStringCasing: 'lyr-persona-highlight-linestring-casing',
            lineString: 'lyr-persona-highlight-linestring',
            circle: 'lyr-persona-highlight-circle'
        }
    });

    config.nearbyContentEl.addEventListener('click', function(e) {
        var whyBtn = e.target.closest('.nm-persona-why-btn');
        if (whyBtn && activeData && activeData.personas) {
            e.preventDefault();
            e.stopPropagation();
            togglePersonaDetails(whyBtn.getAttribute('data-persona-id'));
            return;
        }

        var mapBtn = e.target.closest('.nm-persona-map-btn');
        if (mapBtn && activeData) {
            e.preventDefault();
            e.stopPropagation();
            showScopeOnMap(mapBtn);
            return;
        }

        var signalToggleBtn = e.target.closest('.nm-persona-signal-toggle');
        if (signalToggleBtn && activeData) {
            e.preventDefault();
            e.stopPropagation();
            toggleSignalDetails(
                signalToggleBtn.getAttribute('data-persona-id'),
                signalToggleBtn.getAttribute('data-section'),
                signalToggleBtn.getAttribute('data-signal-id')
            );
            return;
        }

        var groupToggleBtn = e.target.closest('.nm-persona-group-toggle');
        if (groupToggleBtn && activeData) {
            e.preventDefault();
            e.stopPropagation();
            toggleGroupDetails(
                groupToggleBtn.getAttribute('data-persona-id'),
                groupToggleBtn.getAttribute('data-section'),
                groupToggleBtn.getAttribute('data-signal-id'),
                groupToggleBtn.getAttribute('data-group-id')
            );
        }
    });

    function getMessage(key, fallback) {
        return messages[key] || fallback;
    }

    function getPolishPluralForm(count) {
        var absoluteCount = Math.abs(Number(count) || 0);
        var mod100 = absoluteCount % 100;
        var mod10 = absoluteCount % 10;

        if (absoluteCount === 1) {
            return 'one';
        }

        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
            return 'few';
        }

        return 'many';
    }

    function formatPluralCount(count, forms, fallbackForms) {
        var safeCount = Number(count) || 0;
        var resolvedForms = forms || fallbackForms;
        var pluralForm = getPolishPluralForm(safeCount);
        var label = resolvedForms[pluralForm] || resolvedForms.many;

        return String(safeCount) + ' ' + label;
    }

    function formatObjectCount(count) {
        return formatPluralCount(count, messages.objectForms, {
            one: 'obiekt',
            few: 'obiekty',
            many: 'obiektów'
        });
    }

    function formatRadius(radiusMeters) {
        var radius = Number(radiusMeters) || 0;

        if (radius >= 1000) {
            var kilometers = radius / 1000;
            return (Number.isInteger(kilometers) ? String(kilometers) : kilometers.toFixed(1).replace('.', ',')) + ' km';
        }

        return String(radius) + ' m';
    }

    function formatContribution(points) {
        var numeric = Number(points) || 0;
        var sign = numeric > 0 ? '+' : (numeric < 0 ? '-' : '');

        return sign + Math.abs(numeric).toFixed(1).replace('.', ',') + ' pkt';
    }

    function setButtonMode(isActive) {
        if (!config.triggerBtn) {
            return;
        }

        config.triggerBtn.textContent = isActive
            ? getMessage('showObjectsButton', 'Pokaż obiekty')
            : getMessage('showPersonasButton', 'Dla kogo jest?');
        config.triggerBtn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    function resetInlineState() {
        expandedPersonas = {};
        expandedSignals = {};
        expandedGroups = {};
        signalDetailsCache = {};
        signalLoading = {};
        signalErrors = {};
    }

    function abortPending() {
        if (abortController) {
            abortController.abort();
            abortController = null;
        }

        loading = false;
    }

    function abortDetailRequests() {
        Object.keys(detailAbortControllers).forEach(function(key) {
            if (detailAbortControllers[key]) {
                detailAbortControllers[key].abort();
            }
        });

        detailAbortControllers = {};
        detailRequests = {};
        signalLoading = {};
    }

    function closeHighlightPopup() {
        if (highlightPopup) {
            highlightPopup.remove();
            highlightPopup = null;
        }
    }

    function clearHighlightLayer() {
        var map = config.getMap ? config.getMap() : null;
        highlightPulsator.stop();
        if (typeof config.setNearbyContextDimmed === 'function') {
            config.setNearbyContextDimmed(false);
        }
        closeHighlightPopup();

        if (!map) {
            return;
        }

        removeSourceAndLayers(map, HIGHLIGHT_SOURCE_ID, HIGHLIGHT_LAYER_IDS);
    }

    function syncButtonState() {
        if (!config.triggerBtn) {
            return;
        }

        config.triggerBtn.disabled = config.getSelectedLatLng() == null;
        if (!active && !loading) {
            setButtonMode(false);
        }
    }

    function renderLoading() {
        active = true;
        loading = true;
        activeData = null;
        resetInlineState();
        abortDetailRequests();
        clearHighlightLayer();
        setButtonMode(true);
        config.nearbyMetaEl.textContent = getMessage('loadingMeta', 'Dla kogo jest ta okolica?');
        config.nearbyContentEl.innerHTML = '<div class="ms-empty-state nm-loading-state"><strong>'
            + esc(getMessage('loadingTitle', 'Liczenie stereotypów okolicy…'))
            + '</strong><span>'
            + esc(getMessage('loadingBody', 'Sprawdzam, jaki klimat najmocniej wybija się z otoczenia tego miejsca.'))
            + '</span></div>';
    }

    function renderEmpty(data) {
        active = true;
        loading = false;
        activeData = null;
        resetInlineState();
        abortDetailRequests();
        clearHighlightLayer();
        setButtonMode(true);
        config.nearbyMetaEl.textContent = getMessage('emptyMeta', 'Humorystyczny profil okolicy');
        config.nearbyContentEl.innerHTML = '<div class="ms-empty-state nm-empty-state"><strong>'
            + esc(data.summary_headline || getMessage('emptyTitle', 'Ta okolica nie daje się łatwo zaszufladkować'))
            + '</strong><span>'
            + esc(data.summary_copy || getMessage('emptyBody', 'Sygnały są zbyt słabe albo zbyt wymieszane, żeby wygrał jeden mocny stereotyp.'))
            + '</span></div>';
    }

    function renderError() {
        active = true;
        loading = false;
        activeData = null;
        resetInlineState();
        abortDetailRequests();
        clearHighlightLayer();
        setButtonMode(true);
        config.nearbyMetaEl.textContent = getMessage('errorMeta', 'Błąd ładowania profilu');
        config.nearbyContentEl.innerHTML = '<div class="ms-empty-state nm-empty-state"><strong>'
            + esc(getMessage('errorTitle', 'Nie udało się policzyć profilu okolicy'))
            + '</strong><span>'
            + esc(getMessage('errorBody', 'Spróbuj ponownie za chwilę.'))
            + '</span></div>';
    }

    function renderReasonChips(items, modifier) {
        if (!Array.isArray(items) || !items.length) {
            return '';
        }

        return '<div class="nm-nearby-meta-row">' + items.map(function(item) {
            var parts = [item.label, item.metric_summary_short || formatObjectCount(item.count)];
            if (item.closest_dist_m != null) {
                parts.push(getMessage('reasonDistancePrefix', 'od') + ' ' + formatDist(item.closest_dist_m));
            }

            return '<span class="nm-chip ' + (modifier === 'danger' ? 'nm-chip--danger' : 'nm-chip--success') + '">' + esc(parts.join(' • ')) + '</span>';
        }).join('') + '</div>';
    }

    function renderPersonaSections(persona) {
        var sections = [];

        if (persona.reasons && persona.reasons.length) {
            sections.push(
                '<div class="nm-persona-reason-block">'
                + '<div class="nm-persona-section-title">' + esc(getMessage('reasonsTitle', 'Najmocniejsze sygnały')) + '</div>'
                + renderReasonChips(persona.reasons, 'success')
                + '</div>'
            );
        }

        if (persona.caveats && persona.caveats.length) {
            sections.push(
                '<div class="nm-persona-reason-block">'
                + '<div class="nm-persona-section-title">' + esc(getMessage('caveatsTitle', 'Co trochę zgrzyta')) + '</div>'
                + renderReasonChips(persona.caveats, 'danger')
                + '</div>'
            );
        }

        return sections.join('');
    }

    function buildSignalKey(personaId, section, signalId) {
        return [personaId || '', section || '', signalId || ''].join('::');
    }

    function buildGroupKey(personaId, section, signalId, groupId) {
        return [buildSignalKey(personaId, section, signalId), groupId || ''].join('::');
    }

    function togglePersonaDetails(personaId) {
        if (!personaId) {
            return;
        }

        expandedPersonas[personaId] = !expandedPersonas[personaId];
        renderResults(activeData || {});
    }

    function toggleSignalDetails(personaId, section, signalId) {
        var signalKey = buildSignalKey(personaId, section, signalId);
        expandedSignals[signalKey] = !expandedSignals[signalKey];
        renderResults(activeData || {});

        if (expandedSignals[signalKey]) {
            loadSignalDetails(personaId, section, signalId).catch(function() {});
        }
    }

    function toggleGroupDetails(personaId, section, signalId, groupId) {
        var groupKey = buildGroupKey(personaId, section, signalId, groupId);
        expandedGroups[groupKey] = !expandedGroups[groupKey];
        renderResults(activeData || {});
    }

    function findPersona(personaId) {
        if (!activeData || !Array.isArray(activeData.personas)) {
            return null;
        }

        for (var i = 0; i < activeData.personas.length; i++) {
            if (String(activeData.personas[i].id) === String(personaId)) {
                return activeData.personas[i];
            }
        }

        return null;
    }

    function resolveSignalSummaries(persona, section) {
        if (!persona) {
            return [];
        }

        if (section === 'caveat') {
            return Array.isArray(persona.all_caveats) ? persona.all_caveats : [];
        }

        return Array.isArray(persona.all_reasons) ? persona.all_reasons : [];
    }

    function findSignalSummary(persona, section, signalId) {
        var list = resolveSignalSummaries(persona, section);
        for (var i = 0; i < list.length; i++) {
            if (String(list[i].id) === String(signalId)) {
                return list[i];
            }
        }

        return null;
    }

    function buildDetailsUrl(personaId, signalId) {
        if (!activeLatLng) {
            return null;
        }

        return (config.detailsEndpointUrl || 'api/neighborhood-persona-signal-details')
            + '?lat=' + encodeURIComponent(activeLatLng.lat.toFixed(6))
            + '&lng=' + encodeURIComponent(activeLatLng.lng.toFixed(6))
            + '&persona_id=' + encodeURIComponent(personaId)
            + '&signal_id=' + encodeURIComponent(signalId);
    }

    function loadSignalDetails(personaId, section, signalId) {
        var signalKey = buildSignalKey(personaId, section, signalId);
        if (signalDetailsCache[signalKey]) {
            return Promise.resolve(signalDetailsCache[signalKey]);
        }

        if (detailRequests[signalKey]) {
            return detailRequests[signalKey];
        }

        var url = buildDetailsUrl(personaId, signalId);
        if (!url) {
            return Promise.reject(new Error('Missing active persona coordinates'));
        }

        signalLoading[signalKey] = true;
        signalErrors[signalKey] = null;
        if (activeData) {
            renderResults(activeData);
        }

        var controller = new AbortController();
        detailAbortControllers[signalKey] = controller;

        detailRequests[signalKey] = fetch(url, { signal: controller.signal })
            .then(function(response) {
                return response.json().then(function(data) {
                    return { ok: response.ok, data: data || {} };
                });
            })
            .then(function(result) {
                if (!result.ok) {
                    throw new Error(result.data && result.data.error ? result.data.error : getMessage('detailsErrorBody', 'Nie udało się załadować obiektów dla tego sygnału.'));
                }

                signalDetailsCache[signalKey] = result.data;
                signalErrors[signalKey] = null;
                return result.data;
            })
            .catch(function(error) {
                if (error && error.name === 'AbortError') {
                    throw error;
                }

                signalErrors[signalKey] = error && error.message
                    ? error.message
                    : getMessage('detailsErrorBody', 'Nie udało się załadować obiektów dla tego sygnału.');
                throw error;
            })
            .finally(function() {
                delete detailAbortControllers[signalKey];
                delete detailRequests[signalKey];
                delete signalLoading[signalKey];
                if (activeData) {
                    renderResults(activeData);
                }
            });

        return detailRequests[signalKey];
    }

    function collectSignalFeatures(details) {
        if (!details || !Array.isArray(details.groups)) {
            return [];
        }

        var features = [];
        details.groups.forEach(function(group) {
            features = features.concat(collectGroupFeatures(group, details.signal));
        });

        return features;
    }

    function collectGroupFeatures(group, signal) {
        if (!group || !Array.isArray(group.items)) {
            return [];
        }

        return group.items.map(function(item) {
            return buildHighlightFeature(item, signal);
        }).filter(Boolean);
    }

    function buildHighlightFeature(item, signal) {
        if (!item || !item.geometry) {
            return null;
        }

        return {
            type: 'Feature',
            geometry: item.geometry,
            properties: {
                id: item.id,
                label: item.label,
                detail_url: item.detail_url,
                group_label: item.group_label,
                layer_label: item.layer_label,
                object_label: item.object_label,
                signal_label: signal && signal.label ? signal.label : null,
            }
        };
    }

    function findGroupById(details, groupId) {
        if (!details || !Array.isArray(details.groups)) {
            return null;
        }

        for (var i = 0; i < details.groups.length; i++) {
            if (String(details.groups[i].id) === String(groupId)) {
                return details.groups[i];
            }
        }

        return null;
    }

    function findItemById(group, itemId) {
        if (!group || !Array.isArray(group.items)) {
            return null;
        }

        for (var i = 0; i < group.items.length; i++) {
            if (String(group.items[i].id) === String(itemId)) {
                return group.items[i];
            }
        }

        return null;
    }

    function fetchSignalDetailsSilent(personaId, section, signalId) {
        var signalKey = buildSignalKey(personaId, section, signalId);
        if (signalDetailsCache[signalKey]) {
            return Promise.resolve(signalDetailsCache[signalKey]);
        }

        if (detailRequests[signalKey]) {
            return detailRequests[signalKey];
        }

        var url = buildDetailsUrl(personaId, signalId);
        if (!url) {
            return Promise.reject(new Error('Missing active persona coordinates'));
        }

        var controller = new AbortController();
        detailAbortControllers[signalKey] = controller;

        var promise = fetch(url, { signal: controller.signal })
            .then(function(response) {
                return response.json().then(function(data) {
                    return { ok: response.ok, data: data || {} };
                });
            })
            .then(function(result) {
                if (!result.ok) {
                    throw new Error(result.data && result.data.error ? result.data.error : getMessage('detailsErrorBody', 'Nie udało się załadować obiektów dla tego sygnału.'));
                }

                signalDetailsCache[signalKey] = result.data;
                return result.data;
            })
            .finally(function() {
                delete detailAbortControllers[signalKey];
                delete detailRequests[signalKey];
            });

        detailRequests[signalKey] = promise;
        return promise;
    }

    function showScopeOnMap(button) {
        var scope = button.getAttribute('data-scope');
        var personaId = button.getAttribute('data-persona-id');
        var section = button.getAttribute('data-section');
        var signalId = button.getAttribute('data-signal-id');
        var groupId = button.getAttribute('data-group-id');
        var itemId = button.getAttribute('data-item-id');

        var originalText = button.textContent;
        button.classList.add('is-loading');
        button.disabled = true;
        button.textContent = getMessage('mapLoadingButton', 'Ładuję…');

        fetchSignalDetailsSilent(personaId, section, signalId)
            .then(function(details) {
                var features = [];

                if (scope === 'item') {
                    var itemGroup = findGroupById(details, groupId);
                    var item = findItemById(itemGroup, itemId);
                    if (item) {
                        var feature = buildHighlightFeature(item, details.signal);
                        if (feature) {
                            features = [feature];
                        }
                    }
                } else if (scope === 'group') {
                    var group = findGroupById(details, groupId);
                    if (group) {
                        features = collectGroupFeatures(group, details.signal);
                    }
                } else {
                    features = collectSignalFeatures(details);
                }

                renderHighlight(features);
            })
            .catch(function(error) {
                if (error && error.name === 'AbortError') {
                    return;
                }

                console.error('Persona signal detail loading failed', error);
            })
            .finally(function() {
                button.classList.remove('is-loading');
                button.disabled = false;
                button.textContent = originalText;
            });
    }

    function ensureHighlightLayers(map) {
        if (!map.getLayer('lyr-persona-highlight-fill')) {
            map.addLayer({
                id: 'lyr-persona-highlight-fill',
                type: 'fill',
                source: HIGHLIGHT_SOURCE_ID,
                filter: ['==', '$type', 'Polygon'],
                paint: getMapHighlightPaint('fill')
            });
        }

        if (!map.getLayer('lyr-persona-highlight-polygon-casing')) {
            map.addLayer({
                id: 'lyr-persona-highlight-polygon-casing',
                type: 'line',
                source: HIGHLIGHT_SOURCE_ID,
                filter: ['==', '$type', 'Polygon'],
                paint: getMapHighlightPaint('polygonCasing')
            });
        }

        if (!map.getLayer('lyr-persona-highlight-line')) {
            map.addLayer({
                id: 'lyr-persona-highlight-line',
                type: 'line',
                source: HIGHLIGHT_SOURCE_ID,
                filter: ['==', '$type', 'Polygon'],
                paint: getMapHighlightPaint('polygonLine')
            });
        }

        if (!map.getLayer('lyr-persona-highlight-linestring-casing')) {
            map.addLayer({
                id: 'lyr-persona-highlight-linestring-casing',
                type: 'line',
                source: HIGHLIGHT_SOURCE_ID,
                filter: ['==', '$type', 'LineString'],
                paint: getMapHighlightPaint('lineStringCasing')
            });
        }

        if (!map.getLayer('lyr-persona-highlight-linestring')) {
            map.addLayer({
                id: 'lyr-persona-highlight-linestring',
                type: 'line',
                source: HIGHLIGHT_SOURCE_ID,
                filter: ['==', '$type', 'LineString'],
                paint: getMapHighlightPaint('lineString')
            });
        }

        if (!map.getLayer('lyr-persona-highlight-circle')) {
            map.addLayer({
                id: 'lyr-persona-highlight-circle',
                type: 'circle',
                source: HIGHLIGHT_SOURCE_ID,
                filter: ['==', '$type', 'Point'],
                paint: getMapHighlightPaint('circle')
            });
        }
    }

    function buildPopupHtml(feature) {
        var props = feature && feature.properties ? feature.properties : {};
        var lines = [];

        lines.push('<div class="nm-popup-card">');
        lines.push('<div class="nm-popup-kicker">' + esc(getMessage('signalObjectsTitle', 'Dopasowane grupy obiektów')) + '</div>');
        lines.push('<div class="nm-popup-title">' + esc(props.label || getMessage('popupObjectFallbackLabel', 'Obiekt Bazy obiektów topograficznych')) + '</div>');
        if (props.signal_label) {
            lines.push('<div class="nm-popup-meta"><b>' + esc(getMessage('popupSignalLabel', 'Sygnał')) + ':</b> ' + esc(props.signal_label) + '</div>');
        }
        if (props.group_label) {
            lines.push('<div class="nm-popup-meta"><b>' + esc(getMessage('popupGroupLabel', 'Grupa')) + ':</b> ' + esc(props.group_label) + '</div>');
        }
        if (props.object_label && props.object_label !== props.label) {
            lines.push('<div class="nm-popup-meta"><b>' + esc(getMessage('popupTypeLabel', 'Typ')) + ':</b> ' + esc(props.object_label) + '</div>');
        }
        if (props.layer_label) {
            lines.push('<div class="nm-popup-meta"><b>' + esc(getMessage('popupLayerLabel', 'Warstwa')) + ':</b> ' + esc(props.layer_label) + '</div>');
        }
        var detailUrl = safeHref(props.detail_url);
        if (detailUrl) {
            lines.push('<a class="nm-popup-link" href="' + esc(detailUrl) + '">' + esc(getMessage('popupDetailLinkLabel', 'Pełne szczegóły obiektu')) + '</a>');
        }
        lines.push('</div>');

        return lines.join('');
    }

    function showHighlightPopup(feature, lngLat) {
        var map = config.getMap ? config.getMap() : null;
        if (!map || !feature) {
            return;
        }

        var anchor = lngLat;
        if (!anchor) {
            var centroid = centroidOfGeometry(feature.geometry);
            if (centroid) {
                anchor = { lng: centroid[0], lat: centroid[1] };
            }
        }

        if (!anchor) {
            return;
        }

        if (!highlightPopup) {
            highlightPopup = new maplibregl.Popup({ maxWidth: '280px', closeButton: true });
        }

        highlightPopup.setLngLat(anchor).setHTML(buildPopupHtml(feature)).addTo(map);
    }

    function ensureHighlightInteractions(map) {
        if (highlightInteractionsBound) {
            return;
        }

        HIGHLIGHT_CLICK_LAYER_IDS.forEach(function(layerId) {
            registerCursorHandlers(map, layerId);
            map.on('click', layerId, function(e) {
                if (!e.features || !e.features.length) {
                    return;
                }

                if (typeof config.consumeHandledMapClick === 'function') {
                    config.consumeHandledMapClick(e);
                }
                e.preventDefault();
                if (e.originalEvent && typeof e.originalEvent.stopPropagation === 'function') {
                    e.originalEvent.stopPropagation();
                }

                showHighlightPopup(e.features[0], e.lngLat);
            });
        });

        highlightInteractionsBound = true;
    }

    function getFeatureBounds(features) {
        if (!Array.isArray(features) || !features.length) {
            return null;
        }

        var bounds = new maplibregl.LngLatBounds();
        var hasCoordinates = false;

        function visit(node) {
            if (!Array.isArray(node)) {
                return;
            }

            if (typeof node[0] === 'number' && typeof node[1] === 'number') {
                bounds.extend([node[0], node[1]]);
                hasCoordinates = true;
                return;
            }

            node.forEach(visit);
        }

        features.forEach(function(feature) {
            if (feature && feature.geometry) {
                visit(feature.geometry.coordinates);
            }
        });

        return hasCoordinates ? bounds : null;
    }

    function focusHighlightFeatures(map, features) {
        if (!map || !Array.isArray(features) || !features.length) {
            return;
        }

        var bounds = getFeatureBounds(features);
        var padding = typeof config.getMapPaddingForPanel === 'function'
            ? config.getMapPaddingForPanel()
            : 60;

        if (bounds && !bounds.isEmpty()) {
            map.fitBounds(padBounds(bounds, 0.08), {
                padding: padding,
                maxZoom: 16,
                duration: 650,
            });
            return;
        }

        var centroid = features[0] && features[0].geometry ? centroidOfGeometry(features[0].geometry) : null;
        if (centroid) {
            map.flyTo({
                center: centroid,
                zoom: 16,
                duration: 450,
                padding: padding,
            });
        }
    }

    function renderHighlight(features) {
        var map = config.getMap ? config.getMap() : null;
        var filteredFeatures = Array.isArray(features) ? features.filter(function(feature) {
            return !!(feature && feature.geometry);
        }) : [];

        if (!map || !filteredFeatures.length) {
            clearHighlightLayer();
            return;
        }

        closeHighlightPopup();
        addOrUpdateGeoJsonSource(map, HIGHLIGHT_SOURCE_ID, {
            type: 'FeatureCollection',
            features: filteredFeatures,
        });
        ensureHighlightLayers(map);
        ensureHighlightInteractions(map);
        if (typeof config.prepareNearbyForHighlight === 'function') {
            config.prepareNearbyForHighlight();
        } else if (typeof config.setNearbyContextDimmed === 'function') {
            config.setNearbyContextDimmed(true);
        }
        highlightPulsator.start('family');
        if (typeof config.bringDataLayersToFront === 'function') {
            config.bringDataLayersToFront();
        }
        focusHighlightFeatures(map, filteredFeatures);
    }

    function renderSignalMeta(summary, fallbackCount) {
        var parts = [summary.metric_summary_long || formatObjectCount(fallbackCount)];

        if (summary.closest_dist_m != null) {
            parts.push(getMessage('closestObjectPrefix', 'najbliższy') + ' ' + formatDist(summary.closest_dist_m));
        }

        return parts.join(' • ');
    }

    function renderSignalStateBlock(type, title, body) {
        return '<div class="nm-persona-state nm-persona-state--' + esc(type) + '">'
            + '<strong>' + esc(title) + '</strong>'
            + '<span>' + esc(body) + '</span>'
            + '</div>';
    }

    function renderItemMeta(item) {
        var parts = [];
        var singleObjectSummary = formatObjectCount(1);

        if (item.object_label && item.object_label !== item.label) {
            parts.push(item.object_label);
        }
        if (item.layer_label) {
            parts.push(item.layer_label);
        }
        if (item.metric_summary_short && item.metric_summary_short !== singleObjectSummary) {
            parts.push(item.metric_summary_short);
        }
        if (item.dist_m != null) {
            parts.push(formatDist(item.dist_m));
        }

        return parts.join(' • ');
    }

    function renderItemRow(personaId, section, signalId, groupId, item, tone) {
        return '<div class="nm-persona-item-row">'
            + '<div class="nm-persona-item-copy">'
            + '<strong>' + esc(item.label) + '</strong>'
            + '<div class="nm-card-subline">' + esc(renderItemMeta(item)) + '</div>'
            + '</div>'
            + '<div class="nm-persona-inline-actions">'
            + '<button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-persona-map-btn" type="button"'
            + ' data-scope="item" data-persona-id="' + esc(personaId) + '" data-section="' + esc(section) + '"'
            + ' data-signal-id="' + esc(signalId) + '" data-group-id="' + esc(groupId) + '" data-item-id="' + esc(String(item.id)) + '">'
            + esc(getMessage('showOnMapButton', 'Pokaż na mapie'))
            + '</button>'
            + (safeHref(item.detail_url)
                ? '<a class="ms-link nm-ext-link nm-ext-link--compact" href="' + esc(safeHref(item.detail_url)) + '">' + esc(getMessage('itemDetailLinkLabel', 'Szczegóły')) + '</a>'
                : '')
            + '</div>'
            + '</div>';
    }

    function renderGroupCard(personaId, section, signalId, group, tone) {
        var groupKey = buildGroupKey(personaId, section, signalId, group.id);
        var isExpanded = !!expandedGroups[groupKey];
        var autoExpand = Number(group.object_count) <= 1;
        var showItems = autoExpand || isExpanded;
        var metaParts = [group.metric_summary_long || formatObjectCount(group.object_count)];

        if (group.closest_dist_m != null) {
            metaParts.push(getMessage('closestObjectPrefix', 'najbliższy') + ' ' + formatDist(group.closest_dist_m));
        }

        return '<article class="nm-persona-group-card' + (tone === 'danger' ? ' nm-persona-group-card--danger' : '') + '">'
            + '<div class="nm-persona-group-head">'
            + '<div class="nm-persona-group-copy">'
            + '<strong>' + esc(group.label) + '</strong>'
            + '<div class="nm-card-subline">' + esc(metaParts.join(' • ')) + '</div>'
            + '</div>'
            + '<div class="nm-persona-inline-actions">'
            + '<button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-persona-map-btn" type="button"'
            + ' data-scope="group" data-persona-id="' + esc(personaId) + '" data-section="' + esc(section) + '"'
            + ' data-signal-id="' + esc(signalId) + '" data-group-id="' + esc(group.id) + '">'
            + esc(getMessage('showOnMapButton', 'Pokaż na mapie'))
            + '</button>'
            + (Number(group.object_count) > 1
                ? '<button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-persona-toggle-btn nm-persona-group-toggle' + (isExpanded ? ' is-active' : '') + '" type="button"'
                    + ' data-persona-id="' + esc(personaId) + '" data-section="' + esc(section) + '"'
                    + ' data-signal-id="' + esc(signalId) + '" data-group-id="' + esc(group.id) + '">'
                    + esc(isExpanded
                        ? getMessage('hideGroupItemsButton', 'Ukryj listę')
                        : getMessage('showGroupItemsButton', 'Pokaż listę'))
                    + '</button>'
                : '')
            + '</div>'
            + '</div>'
            + (showItems
                ? '<div class="nm-persona-item-list">' + group.items.map(function(item) {
                    return renderItemRow(personaId, section, signalId, group.id, item, tone);
                }).join('') + '</div>'
                : '')
            + '</article>';
    }

    function renderSignalDetails(personaId, section, signal, tone) {
        var signalKey = buildSignalKey(personaId, section, signal.id);
        if (!expandedSignals[signalKey]) {
            return '';
        }

        if (signalLoading[signalKey]) {
            return '<div class="nm-persona-signal-detail">'
                + renderSignalStateBlock(
                    'loading',
                    getMessage('detailsLoadingTitle', 'Ładowanie dopasowanych obiektów…'),
                    getMessage('detailsLoadingBody', 'Zbieram grupy i konkretne obiekty, które weszły do tego sygnału.')
                )
                + '</div>';
        }

        if (signalErrors[signalKey]) {
            return '<div class="nm-persona-signal-detail">'
                + renderSignalStateBlock(
                    'error',
                    getMessage('detailsErrorTitle', 'Nie udało się załadować danych'),
                    signalErrors[signalKey]
                )
                + '</div>';
        }

        var details = signalDetailsCache[signalKey];
        if (!details || !Array.isArray(details.groups) || !details.groups.length) {
            return '<div class="nm-persona-signal-detail">'
                + renderSignalStateBlock(
                    'empty',
                    getMessage('signalObjectsTitle', 'Dopasowane grupy obiektów'),
                    getMessage('detailsEmptyBody', 'Ten sygnał nie ma teraz żadnych obiektów do pokazania.')
                )
                + '</div>';
        }

        return '<div class="nm-persona-signal-detail">'
            + '<div class="nm-persona-inline-note">' + esc(getMessage('signalObjectsTitle', 'Dopasowane grupy obiektów')) + '</div>'
            + '<div class="nm-persona-group-list">' + details.groups.map(function(group) {
                return renderGroupCard(personaId, section, signal.id, group, tone);
            }).join('') + '</div>'
            + '</div>';
    }

    function renderSignalCard(persona, signal, section, tone) {
        var signalKey = buildSignalKey(persona.id, section, signal.id);
        var isExpanded = !!expandedSignals[signalKey];

        return '<article class="nm-persona-signal-card' + (tone === 'danger' ? ' nm-persona-signal-card--danger' : '') + '">'
            + '<div class="nm-persona-signal-head">'
            + '<div class="nm-persona-signal-copy">'
            + '<strong>' + esc(signal.label) + '</strong>'
            + '<div class="nm-card-subline">' + esc(renderSignalMeta(signal, signal.count)) + '</div>'
            + '</div>'
            + '<div class="nm-persona-inline-actions">'
            + '<span class="nm-chip ' + (tone === 'danger' ? 'nm-chip--danger' : 'nm-chip--success') + '">' + esc(formatContribution(signal.contribution)) + '</span>'
            + '<button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-persona-map-btn" type="button"'
            + ' data-scope="signal" data-persona-id="' + esc(persona.id) + '" data-section="' + esc(section) + '"'
            + ' data-signal-id="' + esc(signal.id) + '">'
            + esc(getMessage('showOnMapButton', 'Pokaż na mapie'))
            + '</button>'
            + '<button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-persona-toggle-btn nm-persona-signal-toggle' + (isExpanded ? ' is-active' : '') + '" type="button"'
            + ' data-persona-id="' + esc(persona.id) + '" data-section="' + esc(section) + '" data-signal-id="' + esc(signal.id) + '">'
            + esc(isExpanded
                ? getMessage('hideSignalDetailsButton', 'Ukryj obiekty')
                : getMessage('showSignalDetailsButton', 'Pokaż obiekty'))
            + '</button>'
            + '</div>'
            + '</div>'
            + renderSignalDetails(persona.id, section, signal, tone)
            + '</article>';
    }

    function renderSignalSection(persona, signals, section, title, tone) {
        if (!Array.isArray(signals) || !signals.length) {
            return '';
        }

        return '<div class="nm-persona-inline-section">'
            + '<div class="nm-persona-section-title ' + (tone === 'danger' ? 'nm-text-danger' : 'nm-text-success') + '">' + esc(title) + '</div>'
            + '<div class="nm-persona-signal-list">' + signals.map(function(signal) {
                return renderSignalCard(persona, signal, section, tone);
            }).join('') + '</div>'
            + '</div>';
    }

    function renderPersonaInlineDetails(persona) {
        if (!expandedPersonas[persona.id]) {
            return '';
        }

        return '<div class="nm-persona-inline-details">'
            + renderSignalSection(persona, resolveSignalSummaries(persona, 'reason'), 'reason', getMessage('positivePointsTitle', 'Punkty dodatnie'), 'success')
            + renderSignalSection(persona, resolveSignalSummaries(persona, 'caveat'), 'caveat', getMessage('negativePointsTitle', 'Punkty ujemne (zgrzyty)'), 'danger')
            + '</div>';
    }

    function renderPersonaActionRow(persona, defaultLabel) {
        var isExpanded = !!expandedPersonas[persona.id];

        return '<div class="nm-persona-action-row">'
            + '<button class="ms-button ms-button--secondary ms-button--sm ms-button--pill nm-persona-why-btn' + (isExpanded ? ' is-active' : '') + '" type="button" data-persona-id="' + esc(persona.id) + '">'
            + esc(isExpanded ? getMessage('hideDetailsButton', 'Ukryj szczegóły') : defaultLabel)
            + '</button>'
            + '</div>'
            + renderPersonaInlineDetails(persona);
    }

    function renderHero(persona, data) {
        return '<section class="ms-card ms-card--grid nm-summary-card nm-persona-hero" style="--nm-persona-index:0">'
            + '<div class="nm-persona-kicker">' + esc(data.summary_headline || getMessage('heroKicker', 'Dla kogo jest ta okolica?')) + '</div>'
            + '<div class="nm-persona-card-head">'
                + '<div class="nm-persona-card-copy">'
                    + '<strong class="nm-persona-title">' + esc(persona.icon + ' ' + persona.label) + '</strong>'
                    + '<div class="nm-card-subline nm-persona-subtitle">' + esc(persona.subtitle) + '</div>'
                + '</div>'
                + '<div class="nm-persona-scorebox">'
                    + '<strong>' + esc(String(persona.score) + '/100') + '</strong>'
                    + '<span>' + esc(persona.score_label) + '</span>'
                + '</div>'
            + '</div>'
            + '<div class="nm-persona-bar"><span style="width:' + persona.score + '%"></span></div>'
            + '<p class="nm-card-subline">' + esc(
                getMessage('basedOnPrefix', 'Na podstawie')
                + ' ' + formatObjectCount(data.object_count)
                + ' ' + getMessage('withinRadiusPrefix', 'w promieniu')
                + ' ' + formatRadius(data.radius_m)
            ) + '</p>'
            + '<p class="nm-persona-copy">' + esc(persona.message) + '</p>'
            + renderPersonaSections(persona)
            + renderPersonaActionRow(persona, getMessage('whyBtn', 'A dlaczego tak wyszło?'))
            + '</section>';
    }

    function renderSupportingCard(persona, rank, index) {
        return '<section class="ms-card ms-card--grid nm-detail-block nm-persona-card" style="--nm-persona-index:' + index + '">'
            + '<div class="nm-persona-card-head">'
                + '<div class="nm-persona-card-copy">'
                    + '<span class="nm-persona-rank">#' + esc(String(rank)) + '</span>'
                    + '<strong class="nm-persona-title">' + esc(persona.icon + ' ' + persona.label) + '</strong>'
                    + '<div class="nm-card-subline nm-persona-subtitle">' + esc(persona.subtitle) + '</div>'
                + '</div>'
                + '<div class="nm-persona-scorebox">'
                    + '<strong>' + esc(String(persona.score) + '/100') + '</strong>'
                    + '<span>' + esc(persona.score_label) + '</span>'
                + '</div>'
            + '</div>'
            + '<div class="nm-persona-bar"><span style="width:' + persona.score + '%"></span></div>'
            + '<p class="nm-persona-copy">' + esc(persona.message) + '</p>'
            + renderPersonaSections(persona)
            + renderPersonaActionRow(persona, getMessage('secondaryWhyBtn', 'Pokaż wyliczenia'))
            + '</section>';
    }

    function renderResults(data) {
        activeData = data;
        var personas = Array.isArray(data.personas) ? data.personas : [];
        if (!personas.length) {
            renderEmpty(data);
            return;
        }

        active = true;
        loading = false;
        setButtonMode(true);
        config.nearbyMetaEl.textContent = getMessage('resultsMeta', 'Humorystyczny profil okolicy');
        config.nearbyContentEl.innerHTML = '<div class="nm-persona-layout">'
            + renderHero(personas[0], data)
            + (personas.length > 1
                ? '<div class="nm-detail-stack">' + personas.slice(1).map(function(persona, index) {
                    return renderSupportingCard(persona, index + 2, index + 1);
                }).join('') + '</div>'
                : '')
            + '</div>';
    }

    function hide() {
        abortPending();
        abortDetailRequests();
        resetInlineState();
        active = false;
        activeData = null;
        activeLatLng = null;
        clearHighlightLayer();
        setButtonMode(false);
        if (typeof config.refreshNearbyMeta === 'function') {
            config.refreshNearbyMeta();
        }
        if (typeof config.renderNearbyList === 'function') {
            config.renderNearbyList();
        }
    }

    function reset(options) {
        abortPending();
        abortDetailRequests();
        resetInlineState();
        active = false;
        activeData = null;
        activeLatLng = null;
        clearHighlightLayer();
        setButtonMode(false);
        syncButtonState();

        if (options && options.renderNearby === false) {
            return;
        }

        if (typeof config.refreshNearbyMeta === 'function') {
            config.refreshNearbyMeta();
        }
        if (typeof config.renderNearbyList === 'function') {
            config.renderNearbyList();
        }
    }

    function show(latlng) {
        if (!latlng) {
            return;
        }

        abortPending();
        abortDetailRequests();
        resetInlineState();
        clearHighlightLayer();
        activeLatLng = {
            lat: Number(latlng.lat),
            lng: Number(latlng.lng)
        };
        if (typeof config.setTab === 'function') {
            config.setTab('nearby', { snap: 'half', sync: false });
        }
        renderLoading();

        abortController = new AbortController();
        fetch((config.endpointUrl || 'api/neighborhood-personas')
            + '?lat=' + encodeURIComponent(activeLatLng.lat.toFixed(6))
            + '&lng=' + encodeURIComponent(activeLatLng.lng.toFixed(6)), {
            signal: abortController.signal,
        })
            .then(function(response) {
                return response.json().then(function(data) {
                    return { ok: response.ok, data: data || {} };
                });
            })
            .then(function(result) {
                if (abortController == null) {
                    return;
                }
                if (!result.ok) {
                    throw new Error(result.data && result.data.error ? result.data.error : 'Persona profile loading failed');
                }
                renderResults(result.data || {});
            })
            .catch(function(error) {
                if (error && error.name === 'AbortError') {
                    return;
                }
                console.error('Persona profile loading failed', error);
                renderError();
            })
            .finally(function() {
                abortController = null;
                loading = false;
            });
    }

    function toggle(latlng) {
        if (active || loading) {
            hide();
            return;
        }

        show(latlng);
    }

    return {
        hide: hide,
        isActive: function() {
            return active || loading;
        },
        reset: reset,
        syncButtonState: syncButtonState,
        toggle: toggle,
    };
}
